import React, { useState, useEffect, useCallback } from 'react';
import { AnalyticsEventLocation } from '@mb/lib';
import { encodeFilterValue } from '@mb/lib/search';
import { Divider } from '@mui/material';
import { formatImageSource } from '@productSearch/helpers';
import { ProductSearchMaterial } from '@productSearch/types';
import { Button } from 'components/Buttons/Button';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import * as Styled from './MiniPdpContent.styled';
import { sendAnalytics } from '../../api/events';
import { DescriptionContainer } from '../../components/DescriptionContainer/DescriptionContainer';
import { FinishesGroup } from '../../components/FinishesGroup/FinishesGroup';
import { ImagesContainer } from '../../components/ImagesContainer/ImagesContainer';
import { ProductActions } from '../../components/product/ProductActions';
import { ProductInfo } from '../../components/product/ProductInfo';
import { getActiveProductData, getProductFinishes } from '../../helpers';
import {
  handleMiniPdpDetailsClickDataLayer,
  handleMiniPdpProductClickAnalytics,
  handleProductViewedEvent,
  pushSearchAnalyticsDataLayer,
} from '../../helpers/analytics';
import useAddToBagWithValidations from '../../hooks/useAddToBagWithValidations';
import { useVariationsConfig } from '../../hooks/useVariationsConfig';
import { definedTranslation } from '../../i18n';
import {
  currentMaterialSelector,
  gridMaterialsSelector,
} from '../../redux/selectors/materialsSelector';
import {
  setGridMaterials,
  setSelectedChildMaterial,
} from '../../redux/sliceCreators/materialsSlice';
import { DataModalProps } from '../../types';
import { FinishesGroupData } from '../../types/material';
import { ProductDetails } from '../../types/product';

export function MiniPdpContent({
  itemListId = 'mini_pdp',
  itemListName = 'Mini PDP',
  clickedProductGridIndex,
  cartItemsIds = [],
  structuredProjects = [],
  shouldShowContactButton,
  userLang,
  currentProjectData,
  learnMoreNewTab = false,
  wasModalTriggered,
  dataLayerAnalytics,
  onModalTriggered,
  onContactRepClick,
  onPdpClose,
}: DataModalProps) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const finishes = useSelector(gridMaterialsSelector);
  const { limit } = useVariationsConfig();

  const [activeProduct, setActiveProduct] = useState<ProductDetails | null>(
    null,
  );
  const currentMaterial = useSelector(currentMaterialSelector);
  const { handleAddToBag: addToBag, loadingIds } = useAddToBagWithValidations();
  const [isLoading, setIsLoading] = useState(false);

  const translation = {
    detailsButtonLabel: intl?.formatMessage(
      definedTranslation.miniPDP.detailsButtonLabel,
    ),
  };

  const shouldShowPDP = activeProduct != null;

  const sendViewItemAnalytics = useCallback(
    (product: ProductSearchMaterial) => {
      // Analytics
      pushSearchAnalyticsDataLayer(
        [product],
        '',
        dataLayerAnalytics?.listId ?? 'mini_pdp',
        dataLayerAnalytics?.listName ?? 'Mini PDP',
        'view_item',
        dataLayerAnalytics?.eventLocation ?? 'Mini-PDP',
        false,
        false,
        currentProjectData,
      );
    },
    [
      dataLayerAnalytics?.listId,
      dataLayerAnalytics?.listName,
      dataLayerAnalytics?.eventLocation,
      currentProjectData,
    ],
  );

  useEffect(() => {
    const getFinishes = async (product: ProductSearchMaterial | null) => {
      setIsLoading(true);
      const { finishes, parentProduct } = await getProductFinishes(
        product,
        currentProjectData,
      );
      dispatch(setGridMaterials(finishes));
      if (parentProduct != null) {
        setActiveProduct((prev: ProductDetails) => ({
          ...prev,
          sampleNote: parentProduct?.sample_note || prev.sampleNote,
          sizeNote: parentProduct?.size_notes || prev.sizeNote,
          installImages: parentProduct?.install_images?.length
            ? parentProduct.install_images
            : prev.installImages,
        }));
      }
    };

    const handleSetActiveProduct = (product: ProductSearchMaterial | null) => {
      setActiveProduct(null);

      if (product == null) {
        return;
      }

      sendViewItemAnalytics(product);
      handleProductViewedEvent(product);
      const productDetails = getActiveProductData(
        product,
        dataLayerAnalytics?.listId ?? itemListId,
        dataLayerAnalytics?.listName ?? itemListName,
      );
      setActiveProduct(productDetails);
    };

    const initProduct = (currentMaterial: ProductSearchMaterial) => {
      if (currentMaterial != null) {
        getFinishes(currentMaterial);
        handleSetActiveProduct(currentMaterial);
        setIsLoading(false);
      }
    };

    initProduct(currentMaterial);
  }, [currentMaterial]);

  const handleProductClick = async (material: ProductSearchMaterial) => {
    sendAnalytics({
      id: [String(material?.entityId)],
      type: 'view',
      value: 'minipdp',
    });
    handleMiniPdpProductClickAnalytics(material, dataLayerAnalytics);
    sendViewItemAnalytics(material);
    dispatch(setSelectedChildMaterial(material));
  };

  const handleDetailsClick = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    sendAnalytics({
      id: [String(activeProduct?.entityId)],
      type: 'view',
      value: 'mainpdp',
    });

    if (activeProduct?.originalMaterial != null) {
      handleMiniPdpDetailsClickDataLayer({
        product: {
          ...activeProduct?.originalMaterial,
          url: activeProduct?.materialUrl || '',
        },
        isLearnMoreNewTab: learnMoreNewTab,
        dataLayerAnalytics,
      });
    }
  };

  const handleAddedToCart = (
    product: ProductSearchMaterial,
    inFinish: boolean = false,
  ) => {
    addToBag({
      product,
      shouldTriggerRecModal: wasModalTriggered,
      onAddToCartClick: undefined,
      qvLocation: 'variations',
      atcLocation: 'QV',
      isFinish: inFinish,
      relatedProductData: {
        id: currentMaterial?.entityId,
        title: `${currentMaterial?.manufacturer} ${currentMaterial?.name}`,
      },
      dataLayerAnalytics,
    });
    onModalTriggered?.(true);
  };

  const handleCloseTooltip = () => {
    dispatch(setSelectedChildMaterial(undefined));
  };

  const bannerImageUrl = formatImageSource(
    activeProduct?.imageUrl,
    1200,
    false,
    80,
  );
  const prefix = userLang === 'en' ? '' : `/${userLang}`;
  const productCategory = activeProduct?.originalMaterial?.taxonomy?.[0];
  const brandLink = `${prefix}/search?q=#/filter:manufacturer:${encodeFilterValue(
    activeProduct?.brandLabel || '',
  )}#/page:1`;
  const categoryLink = `${prefix}/search?q=#/filter:taxonomy:${encodeFilterValue(
    productCategory || '',
  )}#/page:1`;

  const handleCategoryClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    window.location.href = categoryLink;
    onPdpClose?.(undefined, 'redirect');
  };

  const handleBrandClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    window.location.href = brandLink;
    onPdpClose?.(undefined, 'redirect');
  };

  if (!shouldShowPDP) {
    return null;
  }

  return (
    <Styled.MiniPdpColumnsWrapper>
      <ImagesContainer
        activeProduct={activeProduct}
        isLoading={isLoading}
        mainImage={bannerImageUrl?.src}
        images={activeProduct?.installImages || []}
        itemListId={itemListId}
        itemListName={itemListName}
        clickedProductGridIndex={clickedProductGridIndex}
        modalType={AnalyticsEventLocation.MINI_PDP}
        dataLayerAnalytics={dataLayerAnalytics}
        onCloseTooltip={handleCloseTooltip}
      />
      <Styled.InfoOuterWrapper>
        <ProductInfo
          isLoading={isLoading}
          categoryLink={categoryLink}
          brandLabel={activeProduct?.brandLabel || ''}
          productType={activeProduct?.type || ''}
          productName={activeProduct?.nameLabel || ''}
          brandLink={brandLink}
          onCategoryClick={handleCategoryClick}
          onBrandClick={handleBrandClick}
        />
        <ProductActions
          isLoading={isLoading}
          productEntityId={activeProduct?.entityId || ''}
          shouldShowContactButton={shouldShowContactButton}
          learnMoreButton={
            <Button
              LinkComponent={'a'}
              href={activeProduct?.materialUrl}
              size="medium"
              variant="contained"
              color="primary"
              onClick={handleDetailsClick}
            >
              {translation.detailsButtonLabel}
            </Button>
          }
          onContactRepClick={onContactRepClick}
        />
        <DescriptionContainer
          isLoading={isLoading}
          activeProduct={activeProduct}
        />
        <Divider />

        <div className="flex flex-col w-full gap-4">
          {finishes.map((item: FinishesGroupData, groupIndex: number) => {
            return (
              <FinishesGroup
                key={groupIndex}
                isLoading={isLoading}
                item={item}
                collapseLimit={limit}
                groupIndex={groupIndex}
                clickedProductGridIndex={clickedProductGridIndex}
                activeProduct={activeProduct}
                projects={structuredProjects}
                currentProjectData={currentProjectData}
                cartItemsIds={cartItemsIds}
                loadingCartIds={loadingIds}
                wasModalTriggered={wasModalTriggered}
                onProductClick={handleProductClick}
                onAddToCart={handleAddedToCart}
              />
            );
          })}
        </div>
      </Styled.InfoOuterWrapper>
    </Styled.MiniPdpColumnsWrapper>
  );
}
